/**
 * Where sign-in sends you (issue #257).
 *
 * THE BUG THIS EXISTS FOR. An anonymous visitor opening a shared link —
 * `/coach-portal?tab=feedback` — was bounced to `/login` with the intended
 * destination discarded at that moment: no `?next=`, no `location.state`,
 * nothing anywhere in the app storing it. They signed in and landed on the
 * dashboard. The link appeared to work and quietly didn't do what the sender
 * meant, which is worse than an error because nobody reports it. The landing
 * page's entire premise (#228) is links people send each other, and a
 * first-time visitor is by definition signed out.
 *
 * WHY A VALIDATOR AND NOT JUST `nav(state.from)`. The destination arrives as
 * `location.state`, which react-router stores as `history.state.usr` — data
 * the app was HANDED, not data it computed. One line in a console, or any
 * script running on the page, can put anything there and then point a victim
 * at `/login`. A "return to where you were" that navigates to whatever it
 * finds is an open-redirect primitive: the victim signs in on the real site,
 * with the real URL bar, and is then handed to an attacker's page. So the
 * rule here is allowlist, not blocklist — the target must be one of THIS
 * app's protected routes, and everything else falls back to the dashboard.
 *
 * The order matters: strip first, then validate. Browsers delete tabs,
 * newlines and other control characters from a URL *before* parsing it, so
 * `"/\t/evil.example"` is `"//evil.example"` by the time it is resolved. A
 * validator that checked the raw string and returned it unchanged would
 * approve a string that means something else than what it inspected.
 */

import { PLAYER_HOME } from "./homePath";

/**
 * Where sign-in lands when nothing was captured AND the caller has no role
 * to go on — i.e. the historical default, kept as the fallback's own
 * fallback.
 *
 * It is no longer the whole answer (#384 item 3): a coach's home is My
 * Players, so `Login.jsx` passes `homePathForRole(role)` as the `fallback`
 * argument below the moment it knows the role. This constant stays because
 * `safeRedirectTarget` must have an answer for callers that pass none, and
 * because the security tests below assert "a refused target falls back"
 * against one fixed, role-free value.
 *
 * ISSUE #436 ITEM 3 — IMPORTED, NOT RE-TYPED. This was a second `"/dashboard"`
 * literal beside `homePath.js`'s `PLAYER_HOME`, with the coupling written
 * out in prose in both files and enforced by neither. #384 moved "where a
 * signed-in account belongs" into `homePath.js` precisely so one edit could
 * move it; a literal here is a copy that edit would miss, and the symptom —
 * sign-in landing somewhere the app no longer sends anyone — is exactly the
 * bug #384's own comment says the function exists to prevent. So this is now
 * the same value, by import, and `homePath.test.js` fails if a literal comes
 * back.
 */
export const DEFAULT_AFTER_SIGN_IN = PLAYER_HOME;

/**
 * Every path behind `ProtectedRoute` in `App.js` — the only destinations a
 * signed-out visitor can have been bounced away from, and therefore the only
 * legal targets.
 *
 * Public paths (`/`, `/login`, `/register`, the password-reset pair) are
 * deliberately absent rather than merely unnecessary: they need no session,
 * so they can never be a captured destination, and leaving `/login` out
 * means a hand-crafted state can never bounce sign-in back into itself.
 *
 * Lowercase, no trailing slashes: these are the CANONICAL spellings, and a
 * lookup normalises to them rather than the other way round (see
 * `safeRedirectTarget`, which matches the way the router matches).
 *
 * This is a second copy of a fact `App.js` owns, and a stale copy would fail
 * silently — a route added later would just stop being deep-linkable. The
 * companion test (`redirectTarget.test.js`) reads `App.js` and fails if the
 * two disagree in either direction.
 */
export const PROTECTED_ROUTE_PATHS = Object.freeze([
  "/dashboard",
  // Issue #289. Listed because App.js declares it and the companion test
  // below refuses a router path nobody decided about — not because a shared
  // link to someone else's first-run walkthrough is a thing anyone sends.
  // It is harmless as a target: the page renders for any player, states that
  // they have already been through it when they have, and hands off to the
  // dashboard on finish or skip. There is no loop, because the gate never
  // redirects away from this path.
  "/onboarding",
  // Issue #338. Still listed, and still worth listing now that App.js
  // renders it as a pure <Navigate> to /settings: an anonymous visitor
  // handed a `/profile` link is bounced to sign-in, and this allowlist is
  // what brings them back to `/profile` afterwards so the route's own
  // redirect can land them on Settings. Dropped from here they would land
  // on the dashboard instead — #257's exact symptom, a good link losing its
  // destination at sign-in. (The companion test below would also fail: the
  // router still DECLARES this path.)
  "/profile",
  "/development",
  "/performance",
  "/statistics",
  "/career",
  // Issue #1147: the Dashboard (D228.4); /career redirects here.
  "/insights",
  "/reflections",
  "/grounds",
  "/grounds-map",
  "/coaching-insights",
  "/coach-ai",
  "/ai-insights",
  "/learn",
  // Issues #219/#220 (epic #315) — the app's first parameterised routes
  // (spec appendix: "the first parameterised routes in this app"), so
  // `matchesAllowedPath` below exists because of these three lines. Listed
  // as the router declares them, literally — see that function for how a
  // concrete captured URL (`/learn/batting/cover-drive`) is matched against
  // the `:category`/`:slug` pattern.
  "/learn/review",
  "/learn/:category/:slug",
  "/admin",
  "/admin/guides",
  // Issue #226 — the Users section. Deep-linkable for the same reason its
  // sibling is: an admin handed "go and set Martin up: /admin/users" must
  // land there after signing in, not on a dashboard that has forgotten
  // where they were going (#257).
  "/admin/users",
  // Issue #132 — the Grounds section (the duplicate report). Same reason
  // again, and the test below is what forced the line: a route added to
  // App.js without an entry here signs the admin back in at the dashboard
  // and drops the link they were sent.
  "/admin/grounds",
  // #166 Q4: the admin venue console. Same reasoning as its siblings — a
  // deep link to it must survive sign-in rather than land on the dashboard.
  "/admin/venues",
  // #1191 (D234): the failed-request lookup. A bookmarked link must survive sign-in.
  "/admin/errors",
  // Issue #755. Listed for the same reason /onboarding is: a coach signed
  // out mid-set-up (or handed a bookmark to it) must come back here after
  // signing in rather than landing on the dashboard/portal and losing the
  // destination (#257's symptom). Harmless as a target either way —
  // `CoachSetupGate` decides on arrival whether this coach still belongs
  // here, and there is no loop because the gate never redirects away FROM
  // this path.
  "/coach-setup",
  // Issue #1514: the guided tour. Listed because App.js declares both routes
  // and the companion test refuses a router path nobody decided about. Harmless
  // as a target: the tour renders for any signed-in player or coach and hands
  // off home, so there is no loop.
  "/tour",
  "/tour/coach",
  "/coach-portal",
  // Issue #719 — the coach's read-only view of a linked player's record.
  // Listed for the reason /admin/grounds was: a route App.js declares and
  // this file does not know about signs the coach back in at My Players
  // and drops the link they were sent (#257's symptom), and the companion
  // test refuses the drift outright. The player it names rides along in
  // `?player=`, which `safeRedirectTarget` preserves — the query string
  // being the whole point of this particular link.
  "/coach-portal/record",
  // Issue #1316: Team selection, Your games and Schedule a game. A coach
  // handed one of these links while signed out must come back to it.
  "/coach-portal/team",
  "/coach-portal/games",
  "/coach-portal/games/schedule",
  "/settings",
  // Issue #1032: the delete-account step. The emailed confirm link opens
  // here (`?confirm=<token>`, query preserved), and someone who opens it on
  // a phone that is signed out must come back to it after signing in.
  "/settings/delete-account",
  // Issue #1036: the three pay steps. Stripe sends the payer back to
  // `/plan/return?session_id=...` and `/plan/failed`; a payer whose session
  // ended while they were on Stripe's page must come back to the same place.
  "/plan",
  "/plan/return",
  "/plan/failed",
]);

/**
 * True if `normalized` (already lowercased, trailing-slash-stripped — see
 * `safeRedirectTarget`) matches `pattern` (a literal `PROTECTED_ROUTE_PATHS`
 * entry, possibly carrying `:name` segments).
 *
 * Segment-count equality first, then each segment either matches literally
 * (case-insensitive — `pattern` is already lowercase in this file, and
 * `normalized` was lowercased by the caller) or, for a `:name` segment,
 * matches anything NON-EMPTY. Non-empty matters: `/learn//cover-drive`
 * must not satisfy `:category` with an empty string, because the router
 * itself would not resolve that as this route either.
 */
function matchesAllowedPath(pattern, normalized) {
  if (!pattern.includes(":")) return pattern === normalized;
  const patternParts = pattern.split("/");
  const pathParts = normalized.split("/");
  if (patternParts.length !== pathParts.length) return false;
  return patternParts.every((part, i) => part.startsWith(":") ? pathParts[i].length > 0 : part === pathParts[i]);
}

const ALLOWED_PATHS = new Set(PROTECTED_ROUTE_PATHS);
const PARAMETERISED_PATHS = PROTECTED_ROUTE_PATHS.filter((p) => p.includes(":"));

const MAX_TARGET_LENGTH = 2048;

/**
 * Flattens whatever `location.state.from` turned out to be into one
 * app-relative string. Accepts a react-router location (the idiomatic
 * carrier, and the only thing `ProtectedRoute` ever puts there) or a plain
 * string; anything else is treated as "nothing was captured".
 *
 * `search` and `hash` are carried because they are frequently the whole
 * point of the link — `?tab=feedback` IS the destination the sender meant.
 */
function toRelativePath(from) {
  if (typeof from === "string") return from;
  if (!from || typeof from !== "object" || Array.isArray(from)) return "";
  const { pathname, search, hash } = from;
  if (typeof pathname !== "string" || pathname === "") return "";
  return `${pathname}${typeof search === "string" ? search : ""}${typeof hash === "string" ? hash : ""}`;
}

/**
 * The captured destination if it is safe to navigate to, otherwise
 * `fallback` (the dashboard, i.e. exactly what the app did before #257).
 *
 * Safe means all of: app-relative (one leading slash, never two and never a
 * backslash, which browsers normalise into a protocol-relative URL); no
 * scheme; and a pathname that is a known protected route of this app.
 *
 * `extraPaths` (#1125) lets a caller whose return trip legitimately starts
 * on a PUBLIC screen, such as the privacy notice opened from `/register`,
 * name those canonical paths (lowercase, no trailing slash). Everything
 * else here still applies to them: the same origin rules, the same
 * normalisation. The default is none, so sign-in can still never be sent
 * back into `/login` or `/register`. Must be an array (anything else is
 * treated as none). `["/"]` would admit the root: never pass it from a
 * sign-in caller.
 */
export function safeRedirectTarget(from, fallback = DEFAULT_AFTER_SIGN_IN, { extraPaths = [] } = {}) {
  const extra = new Set(Array.isArray(extraPaths) ? extraPaths : []);
  const raw = toRelativePath(from);
  if (!raw) return fallback;
  // No legal target is anywhere near this long (`?confirm=<token>` and
  // `?player=` are a fraction of it). Cap BEFORE any scan so hostile input
  // costs nothing.
  if (raw.length > MAX_TARGET_LENGTH) return fallback;

  // Everything a browser would drop before parsing (C0 controls incl. tab,
  // LF and CR, plus DEL) — see the header note on ordering.
  const candidate = raw.replace(/[\u0000-\u001F\u007F]/g, "");

  // One leading slash, and only one. `//evil.example` and `/\evil.example`
  // are both protocol-relative URLs to a different origin.
  if (candidate[0] !== "/") return fallback;
  if (candidate[1] === "/" || candidate[1] === "\\") return fallback;

  // The query string and fragment are carried verbatim but are NOT part of
  // what is allowlisted — a fragment cannot change which origin the browser
  // navigates to, and the pathname is what decides that. Their CASE is
  // preserved too: `?player=Aarav` is not `?player=aarav`.
  const suffixAt = candidate.search(/[?#]/);
  const pathname = suffixAt === -1 ? candidate : candidate.slice(0, suffixAt);
  const suffix = suffixAt === -1 ? "" : candidate.slice(suffixAt);

  // Matched the way the ROUTER matches, not more strictly. React Router is
  // case-insensitive by default and tolerates trailing slashes, so
  // `/Coach-Portal` and `/coach-portal/` both render the real page — and an
  // exact-string allowlist would have quietly dropped exactly those links to
  // the dashboard, which is #257's own symptom in a narrower form. A guard
  // that is stricter than the thing it guards does not fail safe, it fails
  // silently.
  // Trailing slashes are stripped with a loop, not `/\/+$/`: that regex is
  // quadratic on a long slash run that is not at the end of the string.
  let end = pathname.length;
  while (end > 1 && pathname[end - 1] === "/") end -= 1;
  const normalized = pathname.slice(0, end).toLowerCase() || "/";
  if (!ALLOWED_PATHS.has(normalized) && !extra.has(normalized) && !PARAMETERISED_PATHS.some((p) => matchesAllowedPath(p, normalized))) {
    return fallback;
  }

  // The CANONICAL spelling, never the caller's: what comes back is a value
  // this module chose from its own allowlist, with only the query and
  // fragment carried over. Returning the input verbatim would mean a
  // validated string and a navigated string that are merely equivalent
  // rather than identical, which is how normalisation bugs get their gap.
  return `${normalized}${suffix}`;
}
